import { useContext, useState, useRef, useEffect, useCallback, useMemo } from "react";
import { Collapse, Spinner } from "reactstrap";
import { _imagesURL, adminBaseURL } from "../../../constants/API";
import Avatar from "../../../assets/images/icon/placeholder_Awatar.png";
import { Language, PostDetailContext } from "../../../App";
import {
  IoThumbsUpOutline,
  IoThumbsUp,
  IoChatbubbleOutline,
  IoBookmarkOutline,
  IoBookmark,
  IoCloseSharp,
} from "react-icons/io5";
import { AiOutlineRetweet } from "react-icons/ai";
import { RiEditLine, RiSparkling2Fill } from "react-icons/ri";
import { MdOutlinePoll, MdTimer } from "react-icons/md";
import { authorize } from "../../../shared/authorize";
import { useCustomToast } from "../../common/CustomToast";
import axios from "axios";
import { UnixToRelative } from "../../notification/Notification";
import ProfileAvatar from "../../common/ProfileAvatar";
import ImageComponent from "../../common/ImageComponent";
import RepostUserModal from "../modals/RepostUserModal";
import usePollRemainingTime from "../../../hooks/usePollRemainingTime";
import { useQueryClient } from "react-query";
import MixpanelManager from "../../../constants/MixpanelManager";
import { MixpanelEvents } from "../../../constants/MixpanelEvents";
import { createPortal } from "react-dom";
import { reactToPostApi } from "../../../services/feedApis";
import "../postTabs/formTheme.css";
import "./PostFooter.css";

export const REACTIONS = [
  { id: "like", emoji: "👍", label: "Like", color: "#00BADA" },
  { id: "love", emoji: "❤️", label: "Love", color: "#F43F5E" },
  { id: "celebrate", emoji: "👏", label: "Celebrate", color: "#10B981" },
  { id: "insightful", emoji: "💡", label: "Insightful", color: "#F59E0B" },
  { id: "fire", emoji: "🔥", label: "Amazing", color: "#FF6B00" },
  { id: "wow", emoji: "😮", label: "Wow", color: "#8B5CF6" },
];

export const calculateTotalReactions = (reactionCounts, fallbackLikesCount = 0) => {
  if (reactionCounts && typeof reactionCounts === "object") {
    if (Array.isArray(reactionCounts)) {
      return reactionCounts.reduce((acc, item) => {
        if (typeof item === "number") return acc + item;
        const cnt = item?.count ?? item?.total ?? item?.votes ?? 0;
        return acc + (Number(cnt) || 0);
      }, 0);
    }
    const vals = Object.values(reactionCounts);
    if (vals.length > 0) {
      return vals.reduce((sum, c) => sum + (Number(c) || 0), 0);
    }
  }
  return Number(fallbackLikesCount || 0);
};

export const getActiveReaction = (reactionName) => {
  if (!reactionName || typeof reactionName !== "string") return null;
  return (
    REACTIONS.find((r) => r.id.toLowerCase() === reactionName.trim().toLowerCase()) || null
  );
};

const resolveAvatar = (src) => {
  if (!src || src === ".." || src === Avatar) return Avatar;
  if (typeof src !== "string") return Avatar;
  const trimmed = src.trim();
  if (!trimmed) return Avatar;
  if (
    trimmed.startsWith("http://") ||
    trimmed.startsWith("https://") ||
    trimmed.startsWith("data:") ||
    trimmed.startsWith("blob:") ||
    trimmed.includes("placeholder_Awatar")
  ) {
    return trimmed;
  }
  const base = _imagesURL.trim().replace(/\/+$/, "");
  const path = trimmed.replace(/^\/+/, "");
  return `${base}/${path}`;
};

const PostFooter = ({
  likes_count,
  setPostId,
  original_id,
  handleLikeUsers,
  like_users_images,
  handleLike,
  isLike,
  handleGetComments,
  comments_count,
  handleRepost,
  isShare,
  isSave,
  handleSave,
  type,
  pollData,
  shares_count,
  value,
}) => {
  const { updatePostDetail } = useContext(PostDetailContext);
  const { lang } = useContext(Language);
  const queryClient = useQueryClient();
  const userData = localStorage.getItem("SignupData");
  const user = userData ? JSON.parse(userData) : null;
  const user_id = user?.data?.id;

  const [isOpen, setIsOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);
  const [showReactions, setShowReactions] = useState(false);

  const getInitialReaction = useCallback(() => {
    const rName = value?.reaction || value?.user_reaction;
    if (rName) {
      const match = getActiveReaction(rName);
      if (match) return match;
    }
    if (isLike || value?.isLike) {
      return REACTIONS[0];
    }
    return null;
  }, [value?.reaction, value?.user_reaction, value?.isLike, isLike]);

  const getInitialLikesCount = useCallback(() => {
    const rc = value?.reaction_counts;
    if (rc && typeof rc === "object") {
      const total = calculateTotalReactions(rc, null);
      if (total !== null && (total > 0 || (typeof rc === "object" && Object.keys(rc).length > 0))) {
        return total;
      }
    }
    return Number(likes_count ?? value?.likes_count ?? 0);
  }, [value?.reaction_counts, likes_count, value?.likes_count]);

  const [currentReaction, setCurrentReaction] = useState(getInitialReaction);
  const [localIsLiked, setLocalIsLiked] = useState(() => Boolean(isLike || value?.reaction || value?.isLike));
  const [localLikesCount, setLocalLikesCount] = useState(getInitialLikesCount);
  const [localLikeUsersImages, setLocalLikeUsersImages] = useState(() => like_users_images || value?.like_users_images || []);
  const [localReactionCounts, setLocalReactionCounts] = useState(() => value?.reaction_counts || {});

  useEffect(() => {
    const initR = getInitialReaction();
    const initCount = getInitialLikesCount();
    setCurrentReaction(initR);
    setLocalIsLiked(Boolean(initR));
    setLocalLikesCount(initCount);
    setLocalLikeUsersImages(like_users_images || value?.like_users_images || []);
    setLocalReactionCounts(value?.reaction_counts || {});
  }, [
    value?.reaction,
    value?.user_reaction,
    value?.isLike,
    isLike,
    likes_count,
    value?.likes_count,
    like_users_images,
    value?.like_users_images,
    value?.reaction_counts,
    getInitialReaction,
    getInitialLikesCount,
  ]);

  const collapseRef = useRef(null);
  const repostButtonRef = useRef(null);
  const hoverTimerRef = useRef(null);
  const leaveTimerRef = useRef(null);
  const longPressTimerRef = useRef(null);
  const isLongPressTriggeredRef = useRef(false);
  const touchStartPosRef = useRef({ x: 0, y: 0 });

  const isPoll =
    type === "poll" ||
    value?.type === "poll" ||
    Boolean(pollData?.option_1) ||
    Boolean(value?.option_1);
  const pid = original_id || value?.original_id || value?.id;
  const isOwner = value?.user?.id === user_id;
  const isReposted = Boolean(isShare ?? value?.isShare);
  const isSaved = Boolean(isSave ?? value?.isSave);
  const currentSharesCount =
    shares_count ??
    value?.shares_count ??
    value?.share_count ??
    value?.reposts_count ??
    value?.shares ??
    0;

  const toggleModal = useCallback(() => {
    setModalOpen((prev) => !prev);
  }, []);

  const toggleCollapse = () => setIsOpen(!isOpen);

  const handleRepostClick = (e) => {
    e?.stopPropagation?.();
    if (!isPoll) {
      if (!isOwner) {
        handleRepost(pid);
      } else {
        setModalOpen(true);
      }
    } else {
      if (isOwner) {
        setModalOpen(true);
      } else {
        if (!isReposted) {
          setIsOpen((prev) => !prev);
        } else {
          handleRepost(pid);
        }
      }
    }
  };

  const handleSaveClick = (e) => {
    e?.stopPropagation?.();
    handleSave(pid);
    MixpanelManager.track(MixpanelEvents.savePost, {
      post_id: pid,
      user_id: user_id,
      is_saved: !isSaved,
    });
  };

  /* ── Reaction Picker Hover Handlers ── */
  const handleLikeMouseEnter = () => {
    if (leaveTimerRef.current) clearTimeout(leaveTimerRef.current);
    hoverTimerRef.current = setTimeout(() => {
      setShowReactions(true);
    }, 220);
  };

  const handleLikeMouseLeave = () => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    leaveTimerRef.current = setTimeout(() => {
      setShowReactions(false);
    }, 320);
  };

  /* ── Long Press Handlers (for Mobile Touch & Hold) ── */
  const startLongPress = (e) => {
    isLongPressTriggeredRef.current = false;
    if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);

    if (e.touches && e.touches[0]) {
      touchStartPosRef.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
      };
    }

    longPressTimerRef.current = setTimeout(() => {
      isLongPressTriggeredRef.current = true;
      setShowReactions(true);
      if (typeof navigator !== "undefined" && navigator.vibrate) {
        try {
          navigator.vibrate(40);
        } catch (err) {}
      }
    }, 360);
  };

  const cancelLongPress = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const handleTouchMove = (e) => {
    if (!longPressTimerRef.current) return;
    if (e.touches && e.touches[0]) {
      const deltaX = Math.abs(e.touches[0].clientX - touchStartPosRef.current.x);
      const deltaY = Math.abs(e.touches[0].clientY - touchStartPosRef.current.y);
      if (deltaX > 10 || deltaY > 10) {
        cancelLongPress();
      }
    }
  };

  const handleTouchEnd = (e) => {
    cancelLongPress();
    if (isLongPressTriggeredRef.current) {
      e?.preventDefault?.();
      e?.stopPropagation?.();
      setTimeout(() => {
        isLongPressTriggeredRef.current = false;
      }, 250);
    }
  };

  const handleSelectReaction = async (e, reaction) => {
    e?.stopPropagation?.();
    setShowReactions(false);
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    if (leaveTimerRef.current) clearTimeout(leaveTimerRef.current);
    if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);

    const activeR = currentReaction || (value?.reaction ? getActiveReaction(value.reaction) : null);
    const wasLiked = Boolean(localIsLiked || activeR || value?.reaction || value?.user_reaction);
    const prevReaction = activeR;
    const prevCounts = { ...(localReactionCounts || {}) };
    const prevLikesCount = localLikesCount;
    const prevImages = [...(localLikeUsersImages || [])];

    const isTogglingOff = Boolean(
      wasLiked &&
      prevReaction?.id &&
      String(prevReaction.id).toLowerCase() === String(reaction.id).toLowerCase()
    );

    let nextIsLiked;
    let nextReaction;
    let nextCounts = { ...prevCounts };
    let nextImages = [...prevImages];

    if (isTogglingOff) {
      nextIsLiked = false;
      nextReaction = null;
      if (nextCounts[reaction.id]) {
        nextCounts[reaction.id] = Math.max(0, Number(nextCounts[reaction.id] || 0) - 1);
        if (nextCounts[reaction.id] === 0) {
          delete nextCounts[reaction.id];
        }
      }
      if (user?.data?.image) {
        nextImages = nextImages.filter((img) => img !== user.data.image);
      }
    } else if (wasLiked) {
      nextIsLiked = true;
      nextReaction = reaction;
      if (prevReaction?.id && nextCounts[prevReaction.id]) {
        nextCounts[prevReaction.id] = Math.max(0, Number(nextCounts[prevReaction.id] || 0) - 1);
        if (nextCounts[prevReaction.id] === 0) {
          delete nextCounts[prevReaction.id];
        }
      }
      nextCounts[reaction.id] = (Number(nextCounts[reaction.id]) || 0) + 1;
      if (user?.data?.image && !nextImages.includes(user.data.image)) {
        nextImages = [user.data.image, ...nextImages];
      }
    } else {
      nextIsLiked = true;
      nextReaction = reaction;
      nextCounts[reaction.id] = (Number(nextCounts[reaction.id]) || 0) + 1;
      if (user?.data?.image && !nextImages.includes(user.data.image)) {
        nextImages = [user.data.image, ...nextImages];
      }
    }

    const nextLikesCount = calculateTotalReactions(
      nextCounts,
      isTogglingOff ? Math.max(0, prevLikesCount - 1) : (wasLiked ? prevLikesCount : prevLikesCount + 1)
    );

    setLocalIsLiked(nextIsLiked);
    setCurrentReaction(nextReaction);
    setLocalLikesCount(nextLikesCount);
    setLocalReactionCounts(nextCounts);
    setLocalLikeUsersImages(nextImages);

    MixpanelManager.track(MixpanelEvents.likePost, {
      post_id: pid,
      user_id: user_id,
      is_liked: nextIsLiked,
      reaction_type: reaction.id,
    });

    try {
      if (handleLike) {
        await handleLike(pid, reaction.id);
      } else {
        await reactToPostApi({ post_id: pid, reaction_type: reaction.id });
      }
    } catch (error) {
      setLocalIsLiked(wasLiked);
      setCurrentReaction(prevReaction);
      setLocalLikesCount(prevLikesCount);
      setLocalReactionCounts(prevCounts);
      setLocalLikeUsersImages(prevImages);
    }
  };

  const handleDirectLikeClick = async (e) => {
    e?.stopPropagation?.();
    if (isLongPressTriggeredRef.current) {
      isLongPressTriggeredRef.current = false;
      return;
    }
    const defaultReaction = REACTIONS[0]; // Like 👍
    const activeR = currentReaction || (value?.reaction ? getActiveReaction(value.reaction) : null);
    const wasLiked = Boolean(localIsLiked || activeR || value?.reaction || value?.user_reaction);
    const targetReaction = wasLiked && activeR ? activeR : defaultReaction;
    await handleSelectReaction(e, targetReaction);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      // If clicking inside any modal or popup, never dismiss or toggle footer state
      const isClickInsideModal = event.target.closest?.(".modal.d-block, .modal, .modal-dialog, .modal-content");
      if (isClickInsideModal) return;

      if (
        collapseRef.current &&
        !collapseRef.current.contains(event.target) &&
        repostButtonRef.current &&
        !repostButtonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }

      const isInsideLikeArea = event.target.closest?.(".mr-action-item, .mr-reactions-container");
      if (!isInsideLikeArea) {
        setShowReactions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside, { passive: true });
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
      if (leaveTimerRef.current) clearTimeout(leaveTimerRef.current);
      if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
    };
  }, []);

  const self_like_image = user?.data?.image;
  const repostTitle = isOwner
    ? `${currentSharesCount || 0} ${lang?.repost || "Reposts"}`
    : isReposted
      ? lang?.reposted || "Reposted"
      : lang?.repost || "Repost";

  const topReactions = useMemo(() => {
    if (
      localReactionCounts &&
      typeof localReactionCounts === "object" &&
      Object.keys(localReactionCounts).length > 0
    ) {
      const items = Object.entries(localReactionCounts)
        .filter(([_, cnt]) => Number(cnt) > 0)
        .map(([rId, cnt]) => {
          const match = REACTIONS.find(
            (r) => r.id.toLowerCase() === rId.toLowerCase()
          );
          return match ? { ...match, count: Number(cnt) } : null;
        })
        .filter(Boolean)
        .sort((a, b) => b.count - a.count)
        .slice(0, 3);
      if (items.length > 0) return items;
    }
    if (localIsLiked && currentReaction) {
      return [
        {
          id: currentReaction.id,
          emoji: currentReaction.emoji,
          label: currentReaction.label,
          count: 1,
        },
      ];
    }
    return [];
  }, [localReactionCounts, localIsLiked, currentReaction]);

  return (
    <>
      <div className="mr-post-footer">
        {/* ── Social Proof / Likers & Activity Section ── */}
        {(localLikesCount > 0 || comments_count > 0 || currentSharesCount > 0) && (
          <div className="mr-social-proof-bar">
            {localLikesCount > 0 ? (
              <div
                className="mr-likers-trigger"
                onClick={() => {
                  setPostId(pid);
                  handleLikeUsers(pid);
                  MixpanelManager.track(MixpanelEvents.listOfLikes, {
                    post_id: pid,
                    user_id: user_id,
                  });
                }}
                role="button"
                tabIndex={0}
              >
                {/* Overlapping Avatars Stack */}
                <div className="mr-avatar-stack">
                  {localLikeUsersImages && localLikeUsersImages.length > 0 ? (
                    localLikeUsersImages.slice(0, 3).map((imgData, index) => (
                      <div
                        key={`liker-${index}`}
                        className="mr-avatar-item"
                        style={{ zIndex: 5 - index }}
                      >
                        <ProfileAvatar
                          src={imgData}
                          size={18}
                          variant="plain"
                          alt="Liker"
                        />
                      </div>
                    ))
                  ) : (
                    <div className="mr-avatar-item" style={{ zIndex: 5 }}>
                      <ProfileAvatar
                        src={self_like_image}
                        size={18}
                        variant="plain"
                        alt="Liker"
                      />
                    </div>
                  )}
                </div>

                {/* Top Reaction Emojis Badges */}
                {topReactions.length > 0 && (
                  <div className="mr-top-reactions-badges">
                    {topReactions.map((r) => (
                      <span
                        key={r.id}
                        className="mr-top-reaction-emoji"
                        title={`${r.label}: ${r.count}`}
                      >
                        {r.emoji}
                      </span>
                    ))}
                  </div>
                )}

                {/* Social Proof Text */}
                <p className="mr-social-proof-text">
                  {lang?.reacted_by || "Reacted by"}{" "}
                  {localIsLiked ? (
                    localLikesCount === 1 ? (
                      <strong>{lang?.you || "you"}</strong>
                    ) : (
                      <>
                        <strong>{lang?.you || "you"}</strong> &amp;{" "}
                        <strong>
                          {localLikesCount - 1}{" "}
                          {localLikesCount - 1 === 1
                            ? lang?.person || "person"
                            : lang?.people || "people"}
                        </strong>
                      </>
                    )
                  ) : localLikesCount > 1 ? (
                    <>
                      <strong>{localLikesCount}</strong> {lang?.people || "people"}
                    </>
                  ) : (
                    <strong>1 {lang?.person || "person"}</strong>
                  )}
                </p>
              </div>
            ) : (
              <div />
            )}

            {/* Comments & Reposts count summary */}
            <div className="d-flex align-items-center gap-1">
              {comments_count > 0 && (
                <span
                  className="mr-summary-chip is-interactive"
                  onClick={() => {
                    setPostId(pid);
                    handleGetComments(pid);
                    updatePostDetail(value);
                  }}
                  title={lang?.comments || "Comments"}
                >
                  {comments_count} {comments_count > 1 ? lang?.comments || "Comments" : lang?.comment || "Comment"}
                </span>
              )}
              {currentSharesCount > 0 && (
                <span className="mr-summary-chip">
                  {comments_count > 0 ? "• " : ""}{currentSharesCount} {currentSharesCount > 1 ? lang?.reposts || "Reposts" : lang?.repost || "Repost"}
                </span>
              )}
            </div>
          </div>
        )}

        {/* ── Main Actions Bar ── */}
        <div className="mr-actions-row">
          {/* 1. LIKE / EMOJI REACTION BUTTON */}
          <div
            className="mr-action-item"
            onMouseEnter={handleLikeMouseEnter}
            onMouseLeave={handleLikeMouseLeave}
          >
            {/* Floating Reactions Bar (Fly-up on hover) */}
            {showReactions && (
              <div
                className="mr-reactions-container"
                onMouseEnter={handleLikeMouseEnter}
                onMouseLeave={handleLikeMouseLeave}
              >
                {REACTIONS.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    className="mr-reaction-btn"
                    onClick={(e) => handleSelectReaction(e, r)}
                    onTouchEnd={(e) => handleSelectReaction(e, r)}
                    title={r.label}
                  >
                    <span className="mr-reaction-emoji">{r.emoji}</span>
                    <span className="mr-reaction-tooltip">{r.label}</span>
                  </button>
                ))}
              </div>
            )}

            <button
              type="button"
              className={`mr-action-touch mr-action-touch--like ${localIsLiked ? "is-active" : ""}`}
              onClick={handleDirectLikeClick}
              onTouchStart={startLongPress}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              onTouchCancel={cancelLongPress}
              onMouseDown={startLongPress}
              onMouseUp={cancelLongPress}
              onMouseLeave={cancelLongPress}
              onContextMenu={(e) => {
                if (isLongPressTriggeredRef.current) {
                  e.preventDefault();
                }
              }}
              aria-label={localIsLiked ? "Unlike" : "Like"}
              title={localIsLiked ? (currentReaction?.label || "Unlike") : "Like"}
            >
              <div
                className="mr-icon-aura"
                style={{
                  color: localIsLiked && currentReaction ? currentReaction.color : undefined,
                  background: localIsLiked && currentReaction ? `${currentReaction.color}18` : undefined,
                }}
              >
                {localIsLiked && currentReaction ? (
                  <span className="mr-selected-emoji-badge">{currentReaction.emoji}</span>
                ) : (
                  <IoThumbsUpOutline className="mr-like-svg" size={20} />
                )}
              </div>
              <span
                className="mr-count-text"
                style={{ color: localIsLiked && currentReaction ? currentReaction.color : undefined }}
              >
                {localLikesCount > 0 ? localLikesCount : ""}
                {localLikesCount > 0
                  ? ""
                  : localIsLiked && currentReaction
                    ? currentReaction.label
                    : lang?.like || "Like"}
              </span>
            </button>
          </div>

          {/* 2. COMMENT BUTTON */}
          <div className="mr-action-item">
            <button
              type="button"
              className="mr-action-touch mr-action-touch--comment"
              onClick={() => {
                setPostId(pid);
                handleGetComments(pid);
                updatePostDetail(value);
                MixpanelManager.track(MixpanelEvents.addComment, {
                  post_id: pid,
                  user_id: user_id,
                });
              }}
              aria-label="Comment"
              title={lang?.comments || "Comments"}
            >
              <div className="mr-icon-aura">
                <IoChatbubbleOutline size={19} />
              </div>
              <span className="mr-count-text">
                {comments_count > 0 ? comments_count : ""}
                {comments_count > 0 ? "" : lang?.comments || "Comments"}
              </span>
            </button>
          </div>

          {/* 3. REPOST BUTTON */}
          <div className="mr-action-item">
            {/* Poll Repost Options Popover (Quote Repost vs Instant Repost) */}
            {isPoll && isOpen && (
              <div
                ref={collapseRef}
                className="mr-repost-popover-container"
                onClick={(e) => e.stopPropagation()}
              >
                <RepostCard
                  repost={handleRepost}
                  original_id={pid}
                  toggleCollapse={() => setIsOpen(false)}
                  pollData={pollData || value}
                  onOpenQuoteModal={() => {
                    setIsOpen(false);
                    setQuoteModalOpen(true);
                  }}
                />
              </div>
            )}

            <button
              type="button"
              ref={repostButtonRef}
              className={`mr-action-touch mr-action-touch--repost ${isReposted ? "is-active" : ""}`}
              onClick={(e) => {
                handleRepostClick(e);
                MixpanelManager.track(MixpanelEvents.repost, {
                  post_id: pid,
                  user_id: user_id,
                  is_reposted: !isReposted,
                });
              }}
              aria-label="Repost"
              title={repostTitle}
            >
              <div className="mr-icon-aura">
                <AiOutlineRetweet className="mr-repost-svg" size={20} />
              </div>
              <span className="mr-count-text">
                {currentSharesCount > 0
                  ? currentSharesCount
                  : isReposted
                    ? lang?.reposted || "Reposted"
                    : lang?.repost || "Repost"}
              </span>
            </button>
          </div>

          {/* 4. SAVE / BOOKMARK BUTTON */}
          <div className="mr-action-item">
            <button
              type="button"
              className={`mr-action-touch mr-action-touch--save ${isSaved ? "is-active" : ""}`}
              onClick={handleSaveClick}
              aria-label={isSaved ? "Unsave post" : "Save post"}
              title={isSaved ? lang?.save_description || "Saved" : lang?.unsave_description || "Save"}
            >
              <div className="mr-icon-aura">
                {isSaved ? (
                  <IoBookmark className="mr-bookmark-svg" size={19} />
                ) : (
                  <IoBookmarkOutline className="mr-bookmark-svg" size={19} />
                )}
              </div>
              <span className="mr-count-text">
                {isSaved ? lang?.saved || "Saved" : lang?.save || "Save"}
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* ── Quote Repost Modal for Polls from Footer ── */}
      {quoteModalOpen && (
        <RepostPollPopup
          isOpen={quoteModalOpen}
          onClose={() => setQuoteModalOpen(false)}
          onPost={() => setQuoteModalOpen(false)}
          pollData={pollData || value}
          handleRepost={handleRepost}
        />
      )}

      {modalOpen && (
        <RepostUserModal
          toggleModal={toggleModal}
          modal={modalOpen}
          postId={pid}
        />
      )}
    </>
  );
};

export default PostFooter;

export const RepostCard = ({
  repost,
  original_id,
  toggleCollapse,
  pollData,
  onOpenQuoteModal,
}) => {
  const { lang } = useContext(Language);
  const [isPopupOpen, setPopupOpen] = useState(false);

  const handleOpenPopup = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (onOpenQuoteModal) {
      onOpenQuoteModal();
    } else {
      setPopupOpen(true);
    }
  };
  const handleClosePopup = () => {
    setPopupOpen(false);
    if (toggleCollapse) toggleCollapse();
  };
  const handlePost = () => {
    setPopupOpen(false);
    if (toggleCollapse) toggleCollapse();
  };

  return (
    <>
      <div className="mr-glass-dropdown">
        {/* Quote Repost Option */}
        <div className="mr-glass-dropdown-item" onClick={handleOpenPopup}>
          <div className="mr-glass-icon-circle mr-glass-icon-circle--quote">
            <RiEditLine size={18} />
          </div>
          <div className="mr-glass-item-content">
            <p className="mr-glass-item-title">
              {lang?.repost_with_your_thoughts || "Quote Repost"}
            </p>
            <p className="mr-glass-item-subtitle">
              {lang?.repost_this_poll_with_your_thoughts || "Add your thoughts & share"}
            </p>
          </div>
        </div>

        {/* Instant Repost Option */}
        <div
          className="mr-glass-dropdown-item"
          onClick={(e) => {
            e.stopPropagation();
            repost(original_id);
            if (toggleCollapse) toggleCollapse();
          }}
        >
          <div className="mr-glass-icon-circle mr-glass-icon-circle--instant">
            <AiOutlineRetweet size={19} />
          </div>
          <div className="mr-glass-item-content">
            <p className="mr-glass-item-title">{lang?.repost || "Instant Repost"}</p>
            <p className="mr-glass-item-subtitle">
              {lang?.repost_this_poll_without_your_thoughts || "Instantly repost to feed"}
            </p>
          </div>
        </div>
      </div>

      {isPopupOpen && (
        <RepostPollPopup
          isOpen={isPopupOpen}
          onClose={handleClosePopup}
          onPost={handlePost}
          pollData={pollData}
          toggleRepostCard={toggleCollapse}
          handleRepost={repost}
        />
      )}
    </>
  );
};

export const RepostPollPopup = ({
  isOpen,
  onClose,
  onPost,
  pollData,
  toggleRepostCard,
  handleRepost,
}) => {
  const { lang } = useContext(Language);
  const { showToast } = useCustomToast();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);
  const [caption, setCaption] = useState("");
  const remainingTime = usePollRemainingTime(pollData?.time, pollData?.duration);

  const userData = localStorage.getItem("SignupData");
  const currentUser = userData ? JSON.parse(userData)?.data : null;

  if (!isOpen) return null;

  const repostWithThoughts = async () => {
    if (!caption.trim()) {
      showToast(lang?.caption_is_required || "Caption is required", "error");
      return;
    }
    const targetId = pollData?.original_id || pollData?.id;
    if (!targetId) {
      showToast("Invalid poll ID", "error");
      return;
    }
    try {
      setIsLoading(true);
      if (handleRepost) {
        await handleRepost(targetId, caption.trim());
      } else {
        const response = await axios.post(
          `${adminBaseURL}user/post/repost/${targetId}`,
          { caption: caption.trim() },
          authorize()
        );
        if (response.data?.status) {
          showToast(response.data.message || lang?.post_reposted_successfully || "Poll reposted successfully!", "success");
        } else {
          showToast(response.data?.message || response.data?.action || "Something went wrong.", "error");
        }
      }
      setIsLoading(false);
      setCaption("");
      onClose();
      queryClient.invalidateQueries("feedData");
      if (toggleRepostCard) toggleRepostCard();
      if (onPost) onPost();
    } catch (error) {
      showToast(error.response?.data?.message || error.message || "An error occurred while reposting.", "error");
      console.error("Error reposting:", error);
      setIsLoading(false);
    }
  };

  const handlePopupClick = (e) => {
    e.stopPropagation();
  };

  const pollImage = pollData?.media
    ? Array.isArray(pollData.media)
      ? pollData.media[0]
      : pollData.media
    : null;

  const pollOptions = [
    pollData?.option_1,
    pollData?.option_2,
    pollData?.option_3,
    pollData?.option_4,
  ].filter((data) => data !== "" && data !== null && data !== undefined);

  const optionLetters = ["A", "B", "C", "D"];

  const modalContent = (
    <div
      className="modal d-block mr-quote-repost-backdrop"
      tabIndex="-1"
      style={{
        backgroundColor: "rgba(15, 23, 42, 0.65)",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
        zIndex: 10000,
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        overflowY: "auto",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="modal-dialog modal-dialog-centered w-100 mr-quote-repost-dialog pf-scope-publish"
        style={{ maxWidth: "580px", margin: "auto" }}
        onClick={handlePopupClick}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div
          className="modal-content"
          style={{
            zIndex: 10001,
            borderRadius: "20px",
            border: "1px solid #E2E8F0",
            overflow: "hidden",
            boxShadow: "0 25px 60px -12px rgba(15, 23, 42, 0.28)",
            background: "#FFFFFF",
          }}
        >
          {/* Modal Header */}
          <div
            className="modal-header border-0 d-flex align-items-center justify-content-between"
            style={{
              padding: "16px 22px 14px 22px",
              borderBottom: "1px solid #F1F5F9",
            }}
          >
            <div className="d-flex align-items-center" style={{ gap: "12px" }}>
              <div
                style={{
                  width: "36px",
                  height: "36px",
                  borderRadius: "10px",
                  background: "#E0F7FA",
                  color: "#00BADA",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "18px",
                  flexShrink: 0,
                }}
              >
                <RiEditLine size={19} />
              </div>
              <div>
                <h5 className="modal-title roboto-bold text-dark mb-0 lh-1" style={{ fontSize: "16px" }}>
                  {lang?.repost_with_your_thoughts || "Repost with your thoughts"}
                </h5>
                <p className="text-muted mb-0 mt-1" style={{ fontSize: "12px" }}>
                  {lang?.repost_this_poll_with_your_thoughts || "Add your thoughts and share this poll to your network"}
                </p>
              </div>
            </div>
            <button
              type="button"
              className="pf-modal-close-btn"
              aria-label="Close"
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
            >
              <IoCloseSharp size={18} />
            </button>
          </div>

          {/* Modal Body */}
          <div className="modal-body" style={{ padding: "18px 22px" }}>
            {/* Current user info row (reusable ProfileAvatar matching PostHeader & PostTab) */}
            <div className="d-flex align-items-center" style={{ gap: "12px", marginBottom: "14px" }}>
              <ProfileAvatar
                src={currentUser?.image}
                size={44}
                variant="subtle"
                alt={currentUser?.name || "Current User"}
              />
              <div className="d-flex flex-column">
                <h6 className="fs-15 roboto-bold text-dark mb-0 lh-1">
                  {currentUser?.name || "You"}
                </h6>
                <p className="mb-0 fs-12 roboto-regular mt-1" style={{ color: "#5F6673" }}>
                  {currentUser?.specialization || lang?.repost_to_feed || "Reposting to feed"}
                </p>
              </div>
            </div>

            {/* Thoughts Textarea (Synchronized with PostForm pf-field, pf-label, & pf-textarea) */}
            <div className="pf-field" style={{ marginBottom: "16px" }}>
              <div className="pf-label" style={{ marginBottom: "5px" }}>
                <span className="pf-label-text">
                  <RiSparkling2Fill className="pf-label-icon" size={13} style={{ color: "#00BADA" }} />
                  {lang?.your_thoughts || "Your Thoughts"}
                </span>
                <span className="pf-char-counter">
                  {caption.length}/500
                </span>
              </div>
              <textarea
                className="pf-textarea"
                rows={3}
                placeholder={lang?.please_enter_your_thoughts || "What clinical insights or thoughts would you like to share?"}
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                onClick={handlePopupClick}
                maxLength={500}
                style={{
                  fontSize: "14px",
                  lineHeight: "1.55",
                  minHeight: "82px",
                }}
                autoFocus
              />
            </div>

            {/* Embedded Poll Card Preview (Clean, Modern & Synchronized) */}
            <div
              style={{
                borderRadius: "16px",
                border: "1.5px solid #E2E8F0",
                background: "#F8FAFC",
                padding: "16px 18px",
              }}
            >
              {/* Poll Author Header (matching PostHeader structure) */}
              <div className="d-flex align-items-center mb-3" style={{ gap: "12px" }}>
                <ProfileAvatar
                  src={pollData?.user?.image}
                  size={44}
                  variant="subtle"
                  alt={pollData?.user?.name || "Author"}
                />
                <div className="d-flex flex-column">
                  <h6 className="fs-15 roboto-bold text-dark mb-0 lh-1">
                    {pollData?.user?.name}
                  </h6>
                  <p className="mb-0 fs-12 roboto-regular mt-1" style={{ color: "#5F6673" }}>
                    {lang?.posted || "Posted"} <UnixToRelative unixTimestamp={pollData?.time} />
                  </p>
                </div>
              </div>

              {/* Poll Question / Title */}
              {(pollData?.title || pollData?.caption) && (
                <p
                  className="roboto-bold text-dark mb-3 lh-base"
                  style={{ fontSize: "15px", margin: "6px 0 12px 0" }}
                >
                  {pollData?.title || pollData?.caption}
                </p>
              )}

              {/* Poll Media if present */}
              {pollImage && (
                <div
                  className="mb-3 rounded-3 overflow-hidden d-flex justify-content-center align-items-center"
                  style={{
                    maxHeight: "220px",
                    background: "#0F172A",
                    border: "1px solid #E2E8F0",
                  }}
                >
                  <ImageComponent
                    src={resolveAvatar(pollImage)}
                    alt="Poll Preview"
                    compClassName="img-fluid"
                    img_height="220px"
                    img_width="100%"
                    loader_height="220px"
                    loader_width="100%"
                  />
                </div>
              )}

              {/* Poll Options (matching PollOptionsView styling with white cards on slate background) */}
              <div className="d-flex flex-column mb-3" style={{ gap: "9px" }}>
                {pollOptions.map((option, index) => {
                  const letter = optionLetters[index] || `${index + 1}`;
                  return (
                    <div
                      key={index}
                      className="d-flex align-items-center justify-content-between rounded-3 position-relative"
                      style={{
                        background: "#FFFFFF",
                        border: "1.5px solid #E2E8F0",
                        borderRadius: "12px",
                        padding: "10px 14px",
                      }}
                    >
                      <div className="d-flex align-items-center me-2" style={{ gap: "12px", minWidth: 0 }}>
                        <span
                          className="d-flex align-items-center justify-content-center roboto-bold rounded-circle flex-shrink-0"
                          style={{
                            width: "28px",
                            height: "28px",
                            background: "#E0F7FA",
                            color: "#00BADA",
                            fontSize: "13px",
                          }}
                        >
                          {letter}
                        </span>
                        <span
                          className="roboto-medium text-truncate"
                          style={{ color: "#0F172A", fontSize: "14px" }}
                        >
                          {option}
                        </span>
                      </div>
                      <div className="flex-shrink-0 ms-2">
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center"
                          style={{
                            width: "18px",
                            height: "18px",
                            border: "2px solid #CBD5E1",
                            background: "#FFFFFF",
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Poll Stats Footer (votes count & timer) */}
              <div className="d-flex align-items-center justify-content-between pt-1 px-0.5">
                <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                  <MdOutlinePoll size={18} style={{ color: "#00BADA" }} />
                  <span className="roboto-bold" style={{ color: "#00BADA", fontSize: "13px" }}>
                    {pollData?.votes_count || pollData?.total_votes || 0}{" "}
                    {(pollData?.votes_count || pollData?.total_votes) === 1
                      ? lang?.vote || "vote"
                      : lang?.votes || "votes"}
                  </span>
                </div>
                {pollData?.duration && remainingTime && (
                  <div
                    className="d-flex align-items-center px-3 py-1 rounded-pill"
                    style={{ gap: "6px", background: "#EEF2F6", border: "1px solid #E2E8F0" }}
                  >
                    <MdTimer size={14} style={{ color: "#64748B" }} />
                    <span className="roboto-medium" style={{ color: "#64748B", fontSize: "12px" }}>
                      {remainingTime}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Modal Footer (Synchronized with PostForm pf-footer-row, pf-footer-actions & pf-btn) */}
          <div
            className="pf-footer-row"
            style={{
              padding: "14px 22px",
              borderTop: "1px solid #EEF2F6",
              background: "#FAFCFE",
              margin: 0,
              justifyContent: "flex-end",
            }}
          >
            <div className="pf-footer-actions">
              <button
                type="button"
                className="pf-btn-cancel"
                onClick={(e) => {
                  e.stopPropagation();
                  onClose();
                }}
              >
                {lang?.cancel || "Cancel"}
              </button>
              <button
                type="button"
                className="pf-btn-submit"
                disabled={isLoading || !caption.trim()}
                onClick={(e) => {
                  e.stopPropagation();
                  repostWithThoughts();
                }}
              >
                {isLoading ? (
                  <Spinner size="sm" style={{ color: "#FFFFFF" }} />
                ) : (
                  <>
                    <AiOutlineRetweet size={15} />
                    <span>{lang?.repost || "Repost"}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

